/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ║
║ victor.calle@iudigital.edu.co                          ║
╚════════════════════════════════════════════════════════════╝
*/
import fetchWrapper from "fetchWrapper";

/**
 * Servicio para la gestión de reportes del sistema de carnetización
 * Maneja todas las operaciones relacionadas con la generación y consulta de reportes
 */

/**
 * Obtiene la lista de reportes disponibles con paginación
 * @param {number} pageIndex - Índice de la página a consultar
 * @returns {Promise<Object>} Respuesta con los datos de reportes o error
 */
const getReports = async (pageIndex) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/reports?page=${pageIndex}`
    );

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      const {
        payload: { data, last_page, total, from, to },
      } = result;
      return { data, last_page, total, from, to };
    } else if (success === false) {
      const message = "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

/**
 * Servicio para la gestión de reportes del sistema de carnetización
 * Maneja la obtención del listado de reportes por fuente
 */

/**
 * Obtiene la lista de reportes disponibles con paginación y filtros
 * @param {number} pageIndex - Índice de la página a consultar
 * @param {number} idSource - ID de la fuente a consultar
 * @param {Object} filterParams - Parámetros de filtro (opcional)
 * @returns {Promise<Object>} Respuesta con los datos de reportes o error
 */
const getReportsPerSource = async (pageIndex, idSource, filterParams = {}) => {
  try {
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams({
      page: pageIndex,
      pageSize: 10,
      ...filterParams,
    }).toString();

    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/cards/event_attendance/${idSource}?${queryParams}`
    );

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      const {
        pagination: { last_page, total, from, to },
      } = result;
      const data = result;
      return { data, last_page, total, from, to };
    } else if (success === false) {
      const message = "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

/**
 * Obtiene los datos para generar un reporte específico
 * @param {number} reportId - ID del reporte a generar
 * @param {Object} filters - Filtros aplicados al reporte
 * @returns {Promise<Object>} Datos del reporte o error
 */
const generateReport = async (reportId, filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const url = `${process.env.REACT_APP_API_URL}/reports/${reportId}/generate${
      queryParams ? `?${queryParams}` : ""
    }`;

    const response = await fetchWrapper(url);

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      const { payload } = result;
      return { status: true, data: payload };
    } else if (success === false) {
      const message = result.message || "Error del servidor al generar el reporte";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = "Error del servidor al generar el reporte";
    const status = false;
    return { status, message };
  }
};

/**
 * Descarga un reporte en formato Excel
 * @param {number} reportId - ID del reporte a descargar
 * @returns {Promise<Object>} Archivo del reporte o error
 */
const downloadReportExcel = async (reportId) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/cards/event_attendance/${reportId}/export`;

    const response = await fetchWrapper(url);

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    if (!response.ok) {
      const result = await response.json();
      if (result.success === false) {
        const message = result.message || "Error del servidor al descargar el reporte";
        const status = false;
        return { status, message };
      } else {
        throw new Error("Error del servidor al descargar el reporte");
      }
    }

    // Convertir respuesta a Blob para descarga
    const fileBlob = await response.blob();
    return {
      status: true,
      file: fileBlob,
      contentDisposition: response.headers.get("Content-Disposition"),
    };
  } catch (error) {
    return { status: false, message: error.message };
  }
};

/**
 * Descarga un reporte agrupado de asistencia en formato Excel
 * @param {number} reportId - ID del reporte a descargar
 * @returns {Promise<Object>} Archivo del reporte o error
 */
const downloadSumaryReportExcel = async (reportId) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/cards/event_attendance/${reportId}/export_summary`;

    const response = await fetchWrapper(url);

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    if (!response.ok) {
      const result = await response.json();
      if (result.success === false) {
        const message = result.message || "Error del servidor al descargar el reporte";
        const status = false;
        return { status, message };
      } else {
        throw new Error("Error del servidor al descargar el reporte");
      }
    }

    // Convertir respuesta a Blob para descarga
    const fileBlob = await response.blob();
    return {
      status: true,
      file: fileBlob,
      contentDisposition: response.headers.get("Content-Disposition"),
    };
  } catch (error) {
    return { status: false, message: error.message };
  }
};

/**
 * Descarga un reporte en formato PDF
 * @param {number} reportId - ID del reporte a descargar
 * @param {Object} filters - Filtros aplicados al reporte
 * @returns {Promise<Object>} Archivo del reporte o error
 */
const downloadReportPDF = async (reportId, filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const url = `${process.env.REACT_APP_API_URL}/reports/${reportId}/pdf${
      queryParams ? `?${queryParams}` : ""
    }`;

    const response = await fetchWrapper(url);

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    if (!response.ok) {
      const result = await response.json();
      if (result.success === false) {
        const message = result.message || "Error del servidor al descargar el reporte";
        const status = false;
        return { status, message };
      } else {
        throw new Error("Error del servidor al descargar el reporte");
      }
    }

    // Convertir respuesta a Blob para descarga
    const fileBlob = await response.blob();
    return {
      status: true,
      file: fileBlob,
      contentDisposition: response.headers.get("Content-Disposition"),
    };
  } catch (error) {
    return { status: false, message: error.message };
  }
};

/**
 * Obtiene los tipos de eventos disponibles para filtros
 * @returns {Promise<Object>} Lista de tipos de eventos o error
 */
const getEventTypes = async () => {
  try {
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/event-types`);

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      const { payload } = result;
      return { status: true, data: payload };
    } else if (success === false) {
      const message = "Error del servidor al consultar tipos de eventos";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = "Error del servidor al consultar tipos de eventos";
    const status = false;
    return { status, message };
  }
};

/**
 * Obtiene las áreas/procesos disponibles para filtros
 * @returns {Promise<Object>} Lista de áreas/procesos o error
 */
const getAreas = async () => {
  try {
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/areas`);

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }

    const result = await response.json();
    const { success } = result;

    if (success === true) {
      const { payload } = result;
      return { status: true, data: payload };
    } else if (success === false) {
      const message = "Error del servidor al consultar áreas";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = "Error del servidor al consultar áreas";
    const status = false;
    return { status, message };
  }
};

export {
  getReports,
  getReportsPerSource,
  generateReport,
  downloadReportExcel,
  downloadReportPDF,
  getEventTypes,
  getAreas,
  downloadSumaryReportExcel,
};
